<template>
	<div
		:class="[$style['picture-wrapper'], {
			[$style['picture-wrapper--disabled']] :disabled || requesting || loading
		}]"
		@click="handleUploadClick"
	>
		<div
			v-if="requesting || loading"
			:class="$style['picture-spinner']"
		>
			<Spinner width="40px" />
		</div>
		<template v-else>
			<div
				v-if="(imageError || !imageUrl) && credentials?.length"
				:class="[$style['picture-image'], $style['picture-image--no-image']]"
			>
				{{ credentials }}
			</div>
			<div
				v-else-if="imageError || !imageUrl"
				:class="[$style['picture-image'], $style['picture-image--icon']]"
			>
				<FontAwesomeIcon
					:icon="['fal', 'image']"
					:class="$style['image-wrapper__placeholder']"
				/>
			</div>
			<div
				v-else
				:class="$style['picture-image']"
			>
				<img
					:class="$style['picture-image__img']"
					:src="imageUrl"
					@error="handleImageError"
				/>
			</div>

			<FontAwesomeIcon
				:icon="['fas', 'camera']"
				:class="$style['picture-image__update-icon']"
			/>
		</template>
		<input
			v-if="!disabled"
			ref="upload"
			hidden
			type="file"
			accept="image/*"
			@change="handleFileSelect"
		/>
	</div>
</template>

<script>
import Spinner from '@/components/Essentials/Spinner'
export default {
	name: 'Avatar',
	components: {
		Spinner
	},
	props: {
		loading: {
			type: Boolean,
			default: false
		},
		picture: {
			type: [String],
			default: ''
		},
		credentials: {
			type: [String],
			default: ''
		},
		fullname: {
			type: [String],
			default: ''
		},
		id: {
			type: String,
			default: null
		},
		disabled: {
			type: [String, Boolean, Number],
			default: false
		},
		save: {
			type: [Function, Boolean],
			default: false
		}
	},
	data () {
		return {
			requesting: false,
			imageError: false
		}
	},
	computed: {
		imageUrl () {
			if (this.picture) {
				return this.picture
			}
			if ((this.fullname || this.credentials)) {
				return `https://api.dicebear.com/9.x/initials/svg?size=32&seed=${this.fullname || this.credentials}`.replace(' ', '%20')
			}
			return ''
		}
	},
	watch: {
		imageUrl () {
			this.imageError = false
		}
	},
	methods: {
		async handleFileSelect (e) {
			const file = e.target.files[0]
			if (file) {
				if (!file.type.match('image.*')) {
					return
				}

				const blob = new Blob([file], { type: ['image/png', 'image/jpeg', 'image/gif'] })
				const url = URL.createObjectURL(blob)

				const image = {
					file,
					thumbnail: url
				}

				if (this.save) {
					this.requesting = true
					try {
						await this.save(image)
						this.requesting = false
					} catch (_e) {
						this.requesting = false
					}
				}
			}
		},

		handleImageError () {
			this.imageError = true
		},

		handleUploadClick () {
			if (!this.disabled && !this.requesting) {
				this.$refs.upload.click()
			}
		}
	}
}
</script>
<style module lang="scss">

.picture-wrapper {
    position: relative;
    height: 6vw;
    width: 6vw;
    font-size: 3vw;
    flex-shrink: 0;
    aspect-ratio: 1;

    .picture-spinner {
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: getColor(surface-overlay);
        border-radius: 50%;
        border: 2px solid getColor(surface);
    }

    .picture-image {
		height: 100%;
		width: 100%;
		background-color: getColor(surface);
		border-radius: 50%;
		border: 2px solid getColor(surface);
		position: relative;

		&__img {
			height: 100%;
			width: 100%;
			object-fit: cover;
			border-radius: 50%;
		}

        &--no-image {
            display: flex;
            align-items: center;
            justify-content: center;
            text-transform: uppercase;
            white-space: nowrap;
            letter-spacing: -3px;
            padding-right: 5px;
            font-size: inherit;
            height: 100%;
            width: 100%;
            background-color: getColor(surface-overlay);
            border: 2px solid getColor(surface);
            border-radius: 50%;
            color: getColor(grey);
            font-weight: bold;

            img {
                border-radius: 100%;
            }
        }

        &--icon {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            width: 100%;
            background-color: getColor(surface-overlay);
            border: 2px solid getColor(surface);
            border-radius: 50%;

            .image-wrapper__placeholder {
                color: getColor(disabled-text);
                font-size: 20px;
            }
        }

        &__update-icon {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            opacity: 0;
            z-index: 99;
            color: getColor(white);
            font-size: 36px;
        }

        &::after {
            content: "";
            position: absolute;
            border-radius: 50%;
            inset: 0;
            background-color: getColor(image-overlay-dark);
            opacity: 0;
            transition: opacity 0.2s ease;
        }
    }

    &:hover:not(.picture-wrapper--disabled) {
        cursor: pointer;

        .picture-image__update-icon {
            opacity: 1;
            transition: 0.1s ease;
        }

        .picture-image::after {
            opacity: 1;
        }
    }
}
</style>
